import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../utils/api';
import {
    Activity,
    Calendar,
    Clock,
    Database,
    Globe,
    Info,
    ChevronLeft,
    ChevronRight,
    Search,
    X,
    ShieldCheck,
    ArrowLeft,
    User
} from 'lucide-react';
import { format } from 'date-fns';

interface UserLog {
    _id: string;
    userEmail: string;
    action: string;
    resource: string;
    details: any;
    ipAddress: string;
    userAgent: string;
    status: string;
    page?: string;
    createdAt: string;
    /** Admin id — set only when the action came from "Login as user". */
    impersonatedBy?: string;
}

interface Pagination {
    currentPage: number;
    limit: number;
    total: number;
    totalPages: number;
}

const UserActivityLogs: React.FC = () => {
    const { email } = useParams<{ email: string }>();
    const navigate = useNavigate();
    const [logs, setLogs] = useState<UserLog[]>([]);
    const [loading, setLoading] = useState(true);
    const [pagination, setPagination] = useState<Pagination | null>(null);
    const [page, setPage] = useState(1);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedLog, setSelectedLog] = useState<UserLog | null>(null);

    const fetchLogs = async (p: number) => {
        setLoading(true);
        try {
            const url = email ? `/api/user/logs/${email}` : `/api/user/logs`;
            const response = await api.get(url, {
                params: {
                    page: p,
                    limit: 20,
                    search: searchTerm
                }
            });
            if (response.data.status === 'success') {
                setLogs(response.data.logs);
                setPagination(response.data.pagination);
            }
        } catch (error) {
            console.error("Error fetching user activity logs:", error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        const delayDebounceFn = setTimeout(() => {
            fetchLogs(1);
            setPage(1);
        }, 500);

        return () => clearTimeout(delayDebounceFn);
    }, [searchTerm, email]);

    useEffect(() => {
        fetchLogs(page);
    }, [page]);

    const getStatusColor = (status: string) => {
        return status === 'success' ? 'bg-green-100 text-green-700 border-green-200' : 'bg-red-100 text-red-700 border-red-200';
    };

    return (
        <div className="space-y-6 max-w-7xl mx-auto p-4 md:p-6 pb-20">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div className="flex items-center gap-4">
                    {email && (
                        <button
                            onClick={() => navigate(-1)}
                            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                        >
                            <ArrowLeft className="w-6 h-6 text-gray-600" />
                        </button>
                    )}
                    <div>
                        <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
                            <Activity className="text-[#523680]" />
                            {email ? "User Activity Logs" : "Global User Activities"}
                        </h1>
                        <p className="text-gray-500">
                            {email ? (
                                <>Activity history for <span className="font-semibold text-[#523680]">{email}</span></>
                            ) : (
                                "Monitor all user actions across the application"
                            )}
                        </p>
                    </div>
                </div>

                <div className="flex items-center gap-3">
                    <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
                        <input
                            type="text"
                            placeholder="Search actions or email..."
                            className="pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#523680] outline-none text-sm w-full md:w-64 transition-all"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </div>
                </div>
            </div>

            <div className="bg-white rounded-xl shadow-xl shadow-gray-200/40 border border-gray-100 overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full text-left border-collapse">
                        <thead>
                            <tr className="bg-gray-50 border-b border-gray-100 font-bold uppercase tracking-wider text-[10px] text-gray-400">
                                {!email && <th className="px-6 py-4">User Email</th>}
                                <th className="px-6 py-4">Action</th>
                                <th className="px-6 py-4">Resource</th>
                                <th className="px-6 py-4">Page</th>
                                <th className="px-6 py-4">Status</th>
                                <th className="px-6 py-4">Timestamp</th>
                                <th className="px-6 py-4 text-right">Details</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-50">
                            {loading ? (
                                Array.from({ length: 5 }).map((_, i) => (
                                    <tr key={i} className="animate-pulse">
                                        <td colSpan={email ? 6 : 7} className="px-6 py-4 h-16 bg-gray-50/50"></td>
                                    </tr>
                                ))
                            ) : logs.length > 0 ? (
                                logs.map((log: any) => (
                                    <tr key={log._id} className="hover:bg-gray-50/50 transition-colors group">
                                        {!email && (
                                            <td className="px-6 py-4">
                                                <div className="flex items-center gap-2">
                                                    <div className="w-8 h-8 rounded-full bg-[#523680]/10 flex items-center justify-center text-[#523680] font-bold text-xs">
                                                        {(log.userEmail || 'U').charAt(0).toUpperCase()}
                                                    </div>
                                                    <span className="text-sm font-medium text-gray-700 truncate max-w-[150px]" title={log.userEmail || 'Unknown'}>
                                                        {log.userEmail || 'Unknown User'}
                                                    </span>
                                                </div>
                                            </td>
                                        )}
                                        <td className="px-6 py-4">
                                            <div className="flex items-center gap-2">
                                                <span className="text-sm font-bold text-[#523680]">{log.action}</span>
                                                {log.impersonatedBy && (
                                                    // Present only when an admin performed this through
                                                    // "Login as user" — the account holder did not do it.
                                                    <span
                                                        className="shrink-0 px-2 py-0.5 rounded-full text-[10px] font-black uppercase tracking-tighter bg-amber-50 text-amber-700 border border-amber-200"
                                                        title={`Performed by an admin using "Login as user" (admin id: ${log.impersonatedBy})`}
                                                    >
                                                        Admin
                                                    </span>
                                                )}
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 text-sm text-gray-600">
                                            <div className="inline-flex items-center gap-1.5 bg-gray-100 px-2 py-1 rounded">
                                                <Database className="w-3.5 h-3.5 text-gray-400" />
                                                <span className="font-medium text-[11px] uppercase">{log.resource || 'General'}</span>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4">
                                            <span className="text-xs font-semibold text-gray-500 bg-gray-50 px-2 py-1 rounded border border-gray-100 italic" title={log.page}>
                                                {log.page || 'Direct'}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4">
                                            <span className={`px-2 py-0.5 rounded-full text-[10px] font-black border uppercase tracking-tighter ${getStatusColor(log.status)}`}>
                                                {log.status}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4">
                                            <div className="flex flex-col text-[11px] text-gray-500 font-bold uppercase">
                                                <span className="flex items-center gap-1">
                                                    <Calendar className="w-3 h-3" />
                                                    {format(new Date(log.createdAt), 'MMM dd, yyyy')}
                                                </span>
                                                <span className="flex items-center gap-1 opacity-60">
                                                    <Clock className="w-3 h-3" />
                                                    {format(new Date(log.createdAt), 'hh:mm a')}
                                                </span>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 text-right">
                                            <button
                                                className="p-2 text-gray-400 hover:text-[#523680] hover:bg-[#523680]/10 rounded-lg transition-all"
                                                onClick={() => setSelectedLog(log)}
                                            >
                                                <Info className="w-5 h-5" />
                                            </button>
                                        </td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan={email ? 6 : 7} className="px-6 py-12 text-center text-gray-500 font-medium italic">
                                        No activity logs found.
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>

                {pagination && pagination.totalPages > 1 && (
                    <div className="px-6 py-4 bg-gray-50 border-t border-gray-100 flex items-center justify-between">
                        <span className="text-sm text-gray-500 font-medium">
                            Showing Page {pagination.currentPage} of {pagination.totalPages}
                        </span>
                        <div className="flex items-center gap-2">
                            <button
                                onClick={() => setPage(p => Math.max(1, p - 1))}
                                disabled={page === 1}
                                className="p-2 bg-white border border-gray-200 rounded-lg disabled:opacity-50 hover:shadow-sm transition-all"
                            >
                                <ChevronLeft className="w-4 h-4" />
                            </button>
                            <button
                                onClick={() => setPage(p => Math.min(pagination!.totalPages, p + 1))}
                                disabled={page === pagination.totalPages}
                                className="p-2 bg-white border border-gray-200 rounded-lg disabled:opacity-50 hover:shadow-sm transition-all"
                            >
                                <ChevronRight className="w-4 h-4" />
                            </button>
                        </div>
                    </div>
                )}
            </div>

            {selectedLog && (
                <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60 backdrop-blur-md animate-in fade-in duration-200">
                    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl overflow-hidden animate-in zoom-in-95 duration-200">
                        <div className="px-6 py-5 border-b border-gray-100 flex items-center justify-between bg-gray-50/80">
                            <div className="flex items-center gap-3">
                                <div className="p-2.5 bg-[#523680] text-white rounded-xl shadow-lg shadow-purple-100">
                                    <ShieldCheck className="w-6 h-6" />
                                </div>
                                <div>
                                    <h3 className="font-black text-gray-900 uppercase tracking-tight text-lg">Activity Insight</h3>
                                    <p className="text-[10px] text-gray-400 font-bold uppercase flex items-center gap-2">
                                        ID: {selectedLog._id}
                                        <span className="w-1 h-1 bg-gray-300 rounded-full"></span>
                                        Resource: {selectedLog.resource || 'SYSTEM'}
                                    </p>
                                </div>
                            </div>
                            <button
                                onClick={() => setSelectedLog(null)}
                                className="p-2 hover:bg-gray-200 rounded-full transition-colors group"
                            >
                                <X className="w-5 h-5 text-gray-400 group-hover:text-gray-600" />
                            </button>
                        </div>

                        <div className="p-8 space-y-8 max-h-[75vh] overflow-y-auto">
                            <div className="grid grid-cols-2 gap-6">
                                <div className="p-5 bg-purple-50 rounded-2xl border border-purple-100">
                                    <span className="text-[10px] font-black text-[#523680] uppercase tracking-widest block mb-1">Target Action</span>
                                    <p className="text-sm font-bold text-[#432c69] break-words">{selectedLog.action}</p>
                                </div>
                                <div className="p-5 bg-gray-50 rounded-2xl border border-gray-100">
                                    <span className="text-[10px] font-black text-gray-400 uppercase tracking-widest block mb-1">Result Status</span>
                                    <div className="mt-1">
                                        <span className={`px-2.5 py-1 rounded-full text-[10px] font-black border uppercase tracking-tighter ${getStatusColor(selectedLog.status)}`}>
                                            {selectedLog.status}
                                        </span>
                                    </div>
                                </div>
                            </div>

                            {!email && (
                                <div className="flex items-center gap-3 p-4 bg-gray-50 rounded-2xl border border-gray-100">
                                    <div className="w-10 h-10 rounded-full bg-[#523680] flex items-center justify-center text-white font-black">
                                        {(selectedLog.userEmail || 'U').charAt(0).toUpperCase()}
                                    </div>
                                    <div>
                                        <span className="text-[9px] font-black text-gray-400 uppercase tracking-widest block">Acted By</span>
                                        <p className="text-sm font-bold text-gray-800">{selectedLog.userEmail || 'Unknown User'}</p>
                                    </div>
                                </div>
                            )}

                            <div>
                                <h4 className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-3 flex items-center gap-2">
                                    Technical Payload
                                    <div className="h-[1px] bg-gray-100 flex-grow"></div>
                                </h4>
                                <pre className="p-5 bg-slate-900 rounded-2xl text-[11px] text-blue-400 overflow-x-auto font-mono scrollbar-hide border border-slate-800 shadow-inner">
                                    {JSON.stringify(selectedLog.details, null, 2)}
                                </pre>
                            </div>

                            {/* Page / Origin Info */}
                            {selectedLog.page && (
                                <div className="p-4 bg-amber-50 border border-amber-100 rounded-2xl flex items-center gap-3">
                                    <div className="p-2 bg-amber-100 rounded-xl">
                                        <Info className="w-5 h-5 text-amber-600" />
                                    </div>
                                    <div>
                                        <span className="text-[9px] font-black text-amber-500 uppercase tracking-widest block">Performed From Page</span>
                                        <p className="text-sm font-bold text-amber-800">/{selectedLog.page}</p>
                                    </div>
                                </div>
                            )}

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 pt-4">
                                <div className="space-y-4">
                                    <h4 className="text-[10px] font-black text-gray-400 uppercase tracking-widest flex items-center gap-2">
                                        Network Fingerprint
                                        <div className="h-[1px] bg-gray-100 flex-grow"></div>
                                    </h4>
                                    <div className="space-y-3">
                                        <div className="flex items-center gap-2 text-gray-600">
                                            <Globe className="w-4 h-4 text-[#523680]" />
                                            <span className="text-xs font-bold">IP: {selectedLog.ipAddress || 'Not Tracked'}</span>
                                        </div>
                                        <div className="flex items-start gap-2 text-gray-600 bg-gray-50 p-3 rounded-xl border border-gray-100">
                                            <Activity className="w-4 h-4 mt-0.5 shrink-0" />
                                            <span className="text-[10px] font-medium leading-relaxed italic break-all">{selectedLog.userAgent}</span>
                                        </div>
                                    </div>
                                </div>
                                <div className="space-y-4">
                                    <h4 className="text-[10px] font-black text-gray-400 uppercase tracking-widest flex items-center gap-2">
                                        Chronological Data
                                        <div className="h-[1px] bg-gray-100 flex-grow"></div>
                                    </h4>
                                    <div className="space-y-2">
                                        <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl border border-gray-100">
                                            <Calendar className="w-5 h-5 text-[#523680]" />
                                            <div className="flex flex-col">
                                                <span className="text-[9px] text-gray-400 font-bold uppercase">Date</span>
                                                <span className="text-sm font-bold text-gray-700">{format(new Date(selectedLog.createdAt), 'MMMM dd, yyyy')}</span>
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl border border-gray-100">
                                            <Clock className="w-5 h-5 text-[#523680]" />
                                            <div className="flex flex-col">
                                                <span className="text-[9px] text-gray-400 font-bold uppercase">Precise Time</span>
                                                <span className="text-sm font-bold text-gray-700">{format(new Date(selectedLog.createdAt), 'hh:mm:ss a')}</span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="p-5 bg-gray-50 border-t border-gray-100 flex justify-end px-8">
                            <button
                                onClick={() => setSelectedLog(null)}
                                className="px-8 py-2.5 bg-[#523680] text-white rounded-xl text-xs font-black uppercase tracking-widest hover:bg-[#432c69] transition-all shadow-lg shadow-purple-100 hover:shadow-xl hover:-translate-y-0.5"
                            >
                                Secure Close
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default UserActivityLogs;
